import React, {useCallback, useMemo} from 'react';
import {Link, useSearchParams} from 'react-router-dom';

import {useEventTracking} from 'src/hooks/useEventTracking';

import Group from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import Tabs, {Tab} from '@pb/kit-component-tabs-v2';
import Typography from '@pb/kit-component-typography';

import {useUserPrimaryAccountName} from '../../hooks/use-user-primary-account-name';
import {SUPPORT_EMAIL, buildSupportMailto} from '../../utils/build-support-mailto';

import {AuditLogSection} from './audit-log-section';
import {formatValidityRange, parseCertificateMetadata} from './certificate-utils';

import {
    auditLogSection,
    existingConnectionContainer,
    summaryCard,
} from './existing-connection.module.styl';

import type {ConnectionSettingsResponse} from 'src/openapi/admin/models/ConnectionSettingsResponse';

type ExistingConnectionProps = {
    connection: ConnectionSettingsResponse,
    error: string | null,
};

export const ExistingConnection: React.FC<ExistingConnectionProps> = ({
    connection,
    error,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {configStatusSelect} = useEventTracking();
    const {accountName, accountId} = useUserPrimaryAccountName();
    const supportMailto = useMemo(
        () => buildSupportMailto({accountName, accountId}),
        [accountName, accountId],
    );
    const selectedTab = searchParams.get('tab') || 'configuration';
    const certMetadata = useMemo(
        () => connection.certificate ? parseCertificateMetadata(connection.certificate) : null,
        [connection.certificate],
    );
    const handleConfigurationTabClick = useCallback(() => {
        configStatusSelect();
        setSearchParams({}, {replace: true});
    }, [configStatusSelect, setSearchParams]);

    return (
        <Group direction="v" gap="l" className={existingConnectionContainer}>
            <Group direction="v" gap="xs">
                <Typography variant="heading-level-1">
                    SSO Connection Already Configured
                </Typography>
                <Typography variant="body-level-1">
                    This account already has an SSO connection in place.
                    Need to make changes or remove it? Please contact our support team at&nbsp;
                    <Link to={supportMailto}>
                        {SUPPORT_EMAIL}
                    </Link>
                </Typography>
            </Group>

            {error != null ? (
                <Message color="error">
                    {error}
                </Message>
            ) : null}

            <Tabs tabsId="connection-tabs" tabPanelId="connection-panel" value={selectedTab}>
                <Tab value="configuration" caption="Current Configuration" onClick={handleConfigurationTabClick}/>
                <Tab value="audit-log" caption="Last Updates" onClick={() => setSearchParams({tab: 'audit-log'}, {replace: true})}/>
            </Tabs>

            {selectedTab === 'configuration' ? (
                <div className={summaryCard}>
                    <Table borderedColumns={false}>
                        <Head>
                            <Row>
                                <Cell width={300}>Configuration</Cell>
                                <Cell>Value</Cell>
                            </Row>
                        </Head>
                        <Body>
                            <Row>
                                <Cell>SSO Enabled</Cell>
                                <Cell>{connection.ssoEnabled ? 'Yes' : 'No'}</Cell>
                            </Row>
                            <Row>
                                <Cell>SSO Mandatory</Cell>
                                <Cell>{connection.ssoMandatory ? 'Yes' : 'No'}</Cell>
                            </Row>
                            <Row>
                                <Cell>Login URL</Cell>
                                <Cell>{connection.ssoUrl || '—'}</Cell>
                            </Row>
                            <Row>
                                <Cell>Domains</Cell>
                                <Cell>{connection.domains.length > 0 ? connection.domains.join(', ') : '—'}</Cell>
                            </Row>
                            {certMetadata != null ? (
                                <>
                                    <Row>
                                        <Cell>Certificate</Cell>
                                        <Cell>{formatValidityRange(certMetadata)}</Cell>
                                    </Row>
                                    <Row>
                                        <Cell>Issued To</Cell>
                                        <Cell>{certMetadata.subject}</Cell>
                                    </Row>
                                    <Row>
                                        <Cell>Issued By</Cell>
                                        <Cell>{certMetadata.subject}</Cell>
                                    </Row>
                                </>
                            ) : null}
                        </Body>
                    </Table>
                </div>
            ) : null}

            {selectedTab === 'audit-log' ? (
                <div className={auditLogSection}>
                    <AuditLogSection/>
                </div>
            ) : null}
        </Group>
    );
};
